/**
 * TxStatus — inline transaction lifecycle panel that replaces the swap
 * form while a transaction is in flight. Shows the current step
 * (approving, swapping, confirming) with a spinner, and resolves to a
 * success or failure state with an Etherscan link.
 *
 * Modelled after Uniswap's swap confirmation flow.
 */

import type { TokenSymbol } from "@/lib/tokens";

// ── Types ──────────────────────────────────────────────────────────

export type TxStep =
  | "approve-prompt"     // waiting for wallet to sign approval
  | "approve-pending"    // approval tx submitted, waiting for receipt
  | "simulating"         // pre-flight eth_call dry run
  | "swap-prompt"        // waiting for wallet to sign swap
  | "swap-pending"       // swap tx submitted, waiting for receipt
  | "success"            // swap confirmed
  | "error";             // something failed

export interface TxStatusProps {
  step: TxStep;
  inputToken: TokenSymbol;
  outputToken: TokenSymbol;
  amountIn: string;          // human-readable
  expectedOut: string;       // human-readable
  txHash?: string;
  approveTxHash?: string;
  error?: string;
  onClose: () => void;
}

// ── Component ──────────────────────────────────────────────────────

export function TxStatus({
  step,
  inputToken,
  outputToken,
  amountIn,
  expectedOut,
  txHash,
  approveTxHash,
  error,
  onClose,
}: TxStatusProps) {
  const isPending = step !== "success" && step !== "error";

  return (
    <div className="flex flex-col items-center gap-5 px-2 py-6" role="status" aria-live="polite">
      {/* Spinner or result icon */}
      <div className="relative grid size-16 place-items-center">
        {isPending && <Spinner />}
        {step === "success" && <SuccessIcon />}
        {step === "error" && <ErrorIcon />}
      </div>

      {/* Headline */}
      <div className="text-center">
        <h3 className="text-[17px] font-semibold text-[--text]">
          {headlineFor(step, inputToken, outputToken)}
        </h3>
        <p className="mt-1 text-[13px] text-[--text-muted]">
          {captionFor(step)}
        </p>
      </div>

      {/* Amount summary */}
      <div className="flex w-full max-w-[300px] flex-col gap-2 rounded-[--radius-sm] bg-[--bg-inset] p-3 text-[13px]">
        <Row label="You pay" value={`${amountIn} ${inputToken}`} />
        <Row label="You receive" value={`~${expectedOut} ${outputToken}`} muted={step !== "success"} />
      </div>

      {/* Etherscan links */}
      {approveTxHash && (
        <EtherscanLink hash={approveTxHash} label="Approval tx" />
      )}
      {txHash && (
        <EtherscanLink hash={txHash} label="Swap tx" />
      )}

      {/* Error message */}
      {error && (
        <p className="max-w-[340px] break-words text-center text-[12px] text-[--danger]">
          {error}
        </p>
      )}

      {/* Close / dismiss / cancel button */}
      {(step === "success" || step === "error") && (
        <button
          type="button"
          onClick={onClose}
          className="mt-1 w-full max-w-[300px] rounded-[--radius] px-4 py-3 text-[14px] font-semibold transition bg-[--accent] text-[--accent-on] hover:bg-[--accent-hover]"
        >
          {step === "success" ? "Done" : "Dismiss"}
        </button>
      )}
      {isPending && (
        <button
          type="button"
          onClick={onClose}
          className="mt-1 text-[12px] text-[--text-subtle] hover:text-[--text] transition"
        >
          Cancel
        </button>
      )}
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────

function headlineFor(step: TxStep, inTok: TokenSymbol, outTok: TokenSymbol): string {
  switch (step) {
    case "approve-prompt":
      return `Approve ${inTok}`;
    case "approve-pending":
      return `Approving ${inTok}…`;
    case "simulating":
      return "Simulating swap…";
    case "swap-prompt":
      return "Confirm swap";
    case "swap-pending":
      return "Swap submitted";
    case "success":
      return `Swapped ${inTok} for ${outTok}`;
    case "error":
      return "Transaction failed";
  }
}

function captionFor(step: TxStep): string {
  switch (step) {
    case "approve-prompt":
      return "Confirm the approval in your wallet";
    case "approve-pending":
      return "Waiting for confirmation on chain…";
    case "simulating":
      return "Checking if the swap will succeed…";
    case "swap-prompt":
      return "Confirm the swap in your wallet";
    case "swap-pending":
      return "Waiting for confirmation on chain…";
    case "success":
      return "Your swap has been confirmed";
    case "error":
      return "Something went wrong";
  }
}

function Row({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[--text-muted]">{label}</span>
      <span className={`num min-w-0 truncate text-right ${muted ? "text-[--text-muted]" : "text-[--text]"}`}>
        {value}
      </span>
    </div>
  );
}

function EtherscanLink({ hash, label }: { hash: string; label: string }) {
  return (
    <a
      href={`https://etherscan.io/tx/${hash}`}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-[12px] text-[--accent] hover:underline"
    >
      {label}
      <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3" />
      </svg>
    </a>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin" width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <circle cx="24" cy="24" r="20" stroke="var(--border)" strokeWidth="4" />
      <path d="M24 4a20 20 0 0 1 20 20" stroke="var(--accent)" strokeWidth="4" strokeLinecap="round" />
    </svg>
  );
}

function SuccessIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <circle cx="24" cy="24" r="22" stroke="var(--success)" strokeWidth="3" />
      <path d="M14 24l7 7 13-13" stroke="var(--success)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <circle cx="24" cy="24" r="22" stroke="var(--danger)" strokeWidth="3" />
      <path d="M16 16l16 16M32 16L16 32" stroke="var(--danger)" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}
